import React, { useEffect, useRef, useState } from 'react'
import { StyleSheet, Text, TextInput, View } from 'react-native'

import Button from '../../../UIControls/Button'
import Switch from '../../../UIControls/Switch'
import Spinner from '../../../UIComponents/Spinner'
import styles, { colors } from '../../../styles/global'
import { copyTextToClipboard } from '../../../../utils/HelperFunctions'
import { getBookingSettings, saveBookingSettings } from '../../../../utils/backends/Booking/bookingFirestore'
import { translate } from '../../../../i18n/TranslationService'

const DEFAULT_SETTINGS = {
    enabled: false,
    slug: '',
    durationMinutes: 30,
    availableDurations: [15, 30, 60],
    slotIntervalMinutes: 30,
    workingHoursStart: '09:00',
    workingHoursEnd: '17:00',
    includeWeekends: false,
    // Off by default — a public link must not let strangers book into the day that is
    // already running. The server treats a missing value as false too, so existing links
    // keep this rule without a migration.
    allowSameDayBooking: false,
    bufferBeforeMinutes: 0,
    bufferAfterMinutes: 0,
    // AT-2278 — also honoured by the assistant's meeting-time suggestions, not just this
    // link. The server treats a missing value as 4 too, so existing settings need no migration.
    minFreeHoursPerDay: 4,
    additionalGuestEmails: [],
}

const numericFields = new Set(['durationMinutes', 'slotIntervalMinutes', 'bufferBeforeMinutes', 'bufferAfterMinutes'])
// Hours accept a half hour ("3.5"), so this field keeps a single decimal separator instead
// of having every non-digit stripped like the minute fields above.
const decimalFields = new Set(['minFreeHoursPerDay'])

const sanitizeDecimal = value =>
    String(value)
        .replace(/,/g, '.')
        .replace(/[^0-9.]/g, '')
        .replace(/^(\d*\.?\d*).*$/, '$1')
const durationOptions = [15, 30, 60]

const formatGuestEmails = list => (Array.isArray(list) ? list.join('\n') : '')

export default function PublicBookingSettings() {
    const contentRef = useRef(null)
    const [settings, setSettings] = useState(DEFAULT_SETTINGS)
    const [guestEmailsText, setGuestEmailsText] = useState('')
    const [publicUrl, setPublicUrl] = useState('')
    const [connectedCalendarCount, setConnectedCalendarCount] = useState(0)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [message, setMessage] = useState('')
    const [error, setError] = useState('')

    const loadSettings = async () => {
        try {
            const result = await getBookingSettings()
            setSettings({ ...DEFAULT_SETTINGS, ...(result.settings || {}) })
            setGuestEmailsText(formatGuestEmails(result.settings?.additionalGuestEmails))
            setPublicUrl(result.publicUrl || '')
            setConnectedCalendarCount(result.connectedCalendarCount || 0)
        } catch (loadError) {
            console.error('PublicBookingSettings: failed to load settings', loadError)
            setError(loadError.message || translate('Failed to load booking settings'))
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        loadSettings()
    }, [])

    useEffect(() => {
        const node = contentRef.current
        if (!node || typeof node !== 'object') return undefined
        node.inert = loading
        return () => {
            if (contentRef.current) contentRef.current.inert = false
        }
    }, [loading])

    const updateField = (field, value) => {
        setMessage('')
        setError('')
        setSettings(current => ({
            ...current,
            [field]: numericFields.has(field)
                ? value.replace(/[^0-9]/g, '')
                : decimalFields.has(field)
                  ? sanitizeDecimal(value)
                  : value,
        }))
    }

    const updateGuestEmails = value => {
        setMessage('')
        setError('')
        setGuestEmailsText(value)
    }

    const toggleDuration = duration => {
        setMessage('')
        setError('')
        setSettings(current => {
            const currentDurations = Array.isArray(current.availableDurations) ? current.availableDurations : []
            const nextDurations = currentDurations.includes(duration)
                ? currentDurations.filter(item => item !== duration)
                : [...currentDurations, duration]
            const availableDurations = nextDurations.length > 0 ? nextDurations.sort((a, b) => a - b) : currentDurations
            return {
                ...current,
                availableDurations,
                durationMinutes: availableDurations.includes(parseInt(current.durationMinutes, 10))
                    ? current.durationMinutes
                    : availableDurations[0],
            }
        })
    }

    const getDisplayUrl = () => {
        const slug = String(settings.slug || '').trim()
        if (publicUrl) return publicUrl
        if (!slug) return ''
        if (typeof window !== 'undefined' && window.location?.origin) return `${window.location.origin}/meet/${slug}`
        return `https://my.alldone.app/meet/${slug}`
    }

    const openBookingLink = () => {
        const url = getDisplayUrl()
        if (!url || typeof window === 'undefined') return
        window.open(url, '_blank', 'noopener,noreferrer')
    }

    const onSave = async () => {
        setSaving(true)
        setMessage('')
        setError('')
        try {
            const result = await saveBookingSettings({ ...settings, additionalGuestEmails: guestEmailsText })
            setSettings({ ...DEFAULT_SETTINGS, ...(result.settings || {}) })
            setGuestEmailsText(formatGuestEmails(result.settings?.additionalGuestEmails))
            setPublicUrl(result.publicUrl || '')
            setConnectedCalendarCount(result.connectedCalendarCount || 0)
            setMessage(translate('Booking settings saved'))
        } catch (saveError) {
            console.error('PublicBookingSettings: failed to save settings', saveError)
            setError(saveError.message || translate('Failed to save booking settings'))
        } finally {
            setSaving(false)
        }
    }

    const disabledByCalendar = connectedCalendarCount === 0
    const switchDisabled = loading || (!settings.enabled && disabledByCalendar)

    return (
        <View style={localStyles.container}>
            <View
                ref={contentRef}
                style={loading && localStyles.loadingContent}
                pointerEvents={loading ? 'none' : 'auto'}
                testID="public-booking-settings-content"
            >
                <View style={localStyles.headerRow}>
                    <View style={localStyles.headerText}>
                        <Text style={localStyles.title}>{translate('Public booking link')}</Text>
                        <Text style={localStyles.description}>{translate('Public booking link description')}</Text>
                    </View>
                    <Switch
                        active={settings.enabled}
                        activeSwitch={() => updateField('enabled', true)}
                        deactiveSwitch={() => updateField('enabled', false)}
                        disabled={switchDisabled}
                    />
                </View>

                {!loading && disabledByCalendar && (
                    <Text style={localStyles.warning}>
                        {translate('Connect calendar before enabling booking link')}
                    </Text>
                )}
                <View style={localStyles.durationSection}>
                    <Text style={localStyles.label}>{translate('Meeting duration options')}</Text>
                    <View style={localStyles.durationRow}>
                        {durationOptions.map(duration => {
                            const active = (settings.availableDurations || []).includes(duration)
                            return (
                                <Button
                                    key={duration}
                                    title={translate(`${duration} minute duration`)}
                                    type={active ? 'primary' : 'ghost'}
                                    onPress={() => toggleDuration(duration)}
                                    buttonStyle={localStyles.durationButton}
                                />
                            )
                        })}
                    </View>
                </View>

                <View style={localStyles.grid}>
                    <LabeledInput
                        label={translate('Link slug')}
                        value={settings.slug}
                        onChangeText={v => updateField('slug', v)}
                    />
                    <LabeledInput
                        label={translate('Work starts')}
                        value={settings.workingHoursStart}
                        onChangeText={v => updateField('workingHoursStart', v)}
                    />
                    <LabeledInput
                        label={translate('Work ends')}
                        value={settings.workingHoursEnd}
                        onChangeText={v => updateField('workingHoursEnd', v)}
                    />
                    <LabeledInput
                        label={translate('Buffer before')}
                        value={String(settings.bufferBeforeMinutes)}
                        onChangeText={v => updateField('bufferBeforeMinutes', v)}
                    />
                    <LabeledInput
                        label={translate('Buffer after')}
                        value={String(settings.bufferAfterMinutes)}
                        onChangeText={v => updateField('bufferAfterMinutes', v)}
                    />
                </View>

                <View style={localStyles.freeTimeSection}>
                    <Text style={localStyles.label}>{translate('Minimum free hours per day')}</Text>
                    <Text style={localStyles.guestHint}>{translate('Minimum free hours per day description')}</Text>
                    <TextInput
                        value={String(settings.minFreeHoursPerDay ?? '')}
                        onChangeText={v => updateField('minFreeHoursPerDay', v)}
                        style={[localStyles.input, localStyles.hoursInput]}
                        keyboardType="numeric"
                    />
                </View>

                <View style={localStyles.weekendRow}>
                    <Text style={localStyles.weekendLabel}>{translate('Include weekends')}</Text>
                    <Switch
                        active={settings.includeWeekends}
                        activeSwitch={() => updateField('includeWeekends', true)}
                        deactiveSwitch={() => updateField('includeWeekends', false)}
                    />
                </View>

                <View style={localStyles.toggleRow}>
                    <View style={localStyles.toggleText}>
                        <Text style={localStyles.weekendLabel}>{translate('Allow same day booking')}</Text>
                        <Text style={localStyles.toggleHint}>{translate('Allow same day booking description')}</Text>
                    </View>
                    <Switch
                        active={settings.allowSameDayBooking === true}
                        activeSwitch={() => updateField('allowSameDayBooking', true)}
                        deactiveSwitch={() => updateField('allowSameDayBooking', false)}
                    />
                </View>

                <View style={localStyles.guestSection}>
                    <Text style={localStyles.label}>{translate('Additional guests')}</Text>
                    <Text style={localStyles.guestHint}>{translate('Additional guests description')}</Text>
                    <TextInput
                        value={guestEmailsText}
                        onChangeText={updateGuestEmails}
                        style={[localStyles.input, localStyles.textArea]}
                        multiline
                        placeholder={translate('Additional guests placeholder')}
                        placeholderTextColor={colors.Text03}
                        autoCapitalize="none"
                        autoCorrect={false}
                        keyboardType="email-address"
                    />
                </View>

                {!!getDisplayUrl() && (
                    <View style={localStyles.linkRow}>
                        <Text style={localStyles.linkText}>{getDisplayUrl()}</Text>
                        <Button
                            title={translate('Copy')}
                            type="ghost"
                            onPress={() => {
                                copyTextToClipboard(getDisplayUrl())
                                setMessage(translate('Link copied'))
                            }}
                        />
                        <Button
                            title={translate('Open booking link')}
                            type="ghost"
                            onPress={openBookingLink}
                            buttonStyle={localStyles.openButton}
                        />
                    </View>
                )}

                {!!error && <Text style={localStyles.error}>{error}</Text>}
                {!!message && <Text style={localStyles.success}>{message}</Text>}

                <Button
                    title={translate('Save booking settings')}
                    onPress={onSave}
                    processing={saving}
                    processingTitle={translate('Saving')}
                    disabled={saving}
                    buttonStyle={localStyles.saveButton}
                />
            </View>
            {loading && (
                <View
                    style={localStyles.loadingOverlay}
                    pointerEvents="none"
                    accessibilityRole="progressbar"
                    accessibilityLabel={translate('Loading booking settings')}
                    testID="public-booking-settings-spinner"
                >
                    <Spinner containerSize={64} spinnerSize={40} containerColor={colors.Grey300} />
                </View>
            )}
        </View>
    )
}

function LabeledInput({ label, value, onChangeText }) {
    return (
        <View style={localStyles.inputWrap}>
            <Text style={localStyles.label}>{label}</Text>
            <TextInput value={value} onChangeText={onChangeText} style={localStyles.input} />
        </View>
    )
}

const localStyles = StyleSheet.create({
    container: {
        marginTop: 32,
        paddingTop: 24,
        borderTopWidth: 1,
        borderTopColor: colors.Grey300,
    },
    headerRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
    },
    headerText: {
        flex: 1,
        paddingRight: 16,
    },
    title: {
        ...styles.title6,
        color: colors.Text01,
    },
    description: {
        ...styles.body2,
        color: colors.Text03,
        marginTop: 4,
    },
    loadingContent: {
        opacity: 0.4,
    },
    loadingOverlay: {
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1,
    },
    warning: {
        ...styles.body2,
        color: colors.UtilityRed200,
        marginTop: 12,
    },
    grid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        marginTop: 16,
        marginHorizontal: -6,
    },
    inputWrap: {
        width: 180,
        marginHorizontal: 6,
        marginBottom: 12,
    },
    label: {
        ...styles.caption2,
        color: colors.Text03,
        marginBottom: 4,
    },
    input: {
        height: 40,
        borderWidth: 1,
        borderColor: colors.Grey300,
        borderRadius: 4,
        paddingHorizontal: 10,
        color: colors.Text01,
        backgroundColor: '#FFFFFF',
    },
    weekendRow: {
        flexDirection: 'row',
        alignItems: 'center',
        marginTop: 12,
    },
    toggleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginTop: 16,
    },
    toggleText: {
        flex: 1,
        paddingRight: 16,
    },
    toggleHint: {
        ...styles.caption2,
        color: colors.Text03,
        marginTop: 2,
    },
    guestSection: {
        marginTop: 16,
    },
    freeTimeSection: {
        marginTop: 16,
    },
    hoursInput: {
        width: 180,
        marginTop: 4,
    },
    guestHint: {
        ...styles.caption2,
        color: colors.Text03,
        marginBottom: 8,
    },
    textArea: {
        height: 84,
        paddingTop: 10,
        paddingBottom: 10,
        textAlignVertical: 'top',
    },
    weekendLabel: {
        ...styles.caption2,
        color: colors.Text03,
        marginRight: 12,
    },
    durationSection: {
        marginTop: 16,
    },
    durationRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        marginTop: 6,
        marginHorizontal: -4,
    },
    durationButton: {
        margin: 4,
    },
    linkRow: {
        flexDirection: 'row',
        alignItems: 'center',
        marginTop: 16,
        flexWrap: 'wrap',
    },
    linkText: {
        ...styles.body2,
        width: '100%',
        color: colors.Text02,
        marginBottom: 8,
    },
    openButton: {
        marginLeft: 8,
    },
    error: {
        ...styles.body2,
        color: colors.UtilityRed200,
        marginTop: 12,
    },
    success: {
        ...styles.body2,
        color: colors.UtilityGreen200,
        marginTop: 12,
    },
    saveButton: {
        alignSelf: 'flex-start',
        marginTop: 16,
    },
})
